import { useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AttachFile, Delete, Edit, Mic, Send, Stop } from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import { useVoiceRecorder } from "../../../hooks/useVoiceRecorder";
import {
  addWallComment,
  deleteWallComment,
  editWallComment,
  uploadWallAttachment,
  uploadWallVoice,
} from "../wallSlices";
import { getUploadBaseUrl } from "../../process/processSlices";
import {
  WallAttachment,
  WallComment,
  WallPost,
  WallUser,
} from "../types/wallTypes";
import VoiceNotePlayer from "./VoiceNotePlayer";
import AttachmentGrid from "./AttachmentGrid";
import AttachmentEditList from "./AttachmentEditList";

type Props = {
  post: WallPost;
  officeUsers: WallUser[];
  currentUserId?: string;
  canModerate: boolean;
};

export default function CommentList({
  post,
  officeUsers,
  currentUserId,
  canModerate,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState<WallUser[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editMentions, setEditMentions] = useState<WallUser[]>([]);
  const recorder = useVoiceRecorder();
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<WallAttachment[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingFiles(true);
    for (const file of files) {
      try {
        const att = await dispatch(
          uploadWallAttachment({ file, userId: currentUserId ?? "" }),
        ).unwrap();
        setAttachments((prev) => [...prev, att]);
      } catch {
        // error toast handled in the thunk
      }
    }
    setUploadingFiles(false);
  };

  const submit = async () => {
    if (!text.trim() && !recorder.recording && !attachments.length) return;
    setSubmitting(true);
    let audioUrl: string | undefined;
    if (recorder.recording) {
      const file = new File([recorder.recording.blob], "voice.webm", {
        type: recorder.recording.blob.type,
      });
      try {
        audioUrl = await dispatch(
          uploadWallVoice({ file, userId: currentUserId ?? "" }),
        ).unwrap();
      } catch {
        setSubmitting(false);
        return;
      }
    }
    await dispatch(
      addWallComment({
        postId: post._id!,
        message: text.trim(),
        audioUrl,
        attachments: attachments.map((a) => ({
          url: a.url,
          name: a.name,
          contentType: a.contentType,
        })),
        mentionIds: mentions.map((m) => m._id!).filter(Boolean),
      }),
    );
    setText("");
    setMentions([]);
    setAttachments([]);
    recorder.reset();
    setSubmitting(false);
  };

  const saveEdit = async (commentId: string) => {
    if (!editText.trim()) return;
    await dispatch(
      editWallComment({
        postId: post._id!,
        commentId,
        message: editText.trim(),
        mentionIds: editMentions.map((m) => m._id!).filter(Boolean),
      }),
    );
    setEditingId(null);
  };

  const canEdit = (c: WallComment) => c.author?._id === currentUserId;
  const canDelete = (c: WallComment) =>
    c.author?._id === currentUserId || canModerate;

  return (
    <Box sx={{ mt: 1 }}>
      <Stack spacing={1.5}>
        {(post.comments ?? []).map((c) => (
          <Stack key={c._id} direction="row" spacing={1} alignItems="flex-start">
            <Avatar src={c.author?.avatar} sx={{ width: 28, height: 28 }}>
              {c.author?.name?.[0]}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {c.author?.name}{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  ·{" "}
                  {c.createdAt &&
                    formatDistanceToNow(new Date(c.createdAt), {
                      addSuffix: true,
                    })}
                  {c.editedAt ? ` · ${t("edited")}` : ""}
                </Typography>
              </Typography>
              {editingId === c._id ? (
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={officeUsers}
                    value={editMentions}
                    getOptionLabel={(o) => o.name ?? ""}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    onChange={(_, v) => setEditMentions(v)}
                    renderInput={(params) => (
                      <TextField {...params} label={t("mention")} />
                    )}
                  />
                  <IconButton size="small" onClick={() => saveEdit(c._id!)}>
                    <Send fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <>
                  {c.message && (
                    <Typography variant="body2">{c.message}</Typography>
                  )}
                  {(c.mentions ?? []).length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {c.mentions!.map((m) => (
                        <Typography
                          key={m._id}
                          variant="caption"
                          color="success.main"
                        >
                          @{m.name}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  {c.audioUrl && (
                    <Box sx={{ mt: 0.5 }}>
                      <VoiceNotePlayer
                        src={`${getUploadBaseUrl()}/${c.audioUrl}`}
                      />
                    </Box>
                  )}
                  {c.attachments && c.attachments.length > 0 && (
                    <AttachmentGrid attachments={c.attachments} dense />
                  )}
                </>
              )}
            </Box>
            {canEdit(c) && editingId !== c._id && (
              <IconButton
                size="small"
                onClick={() => {
                  setEditingId(c._id!);
                  setEditText(c.message);
                  setEditMentions(c.mentions ?? []);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
            {canDelete(c) && (
              <IconButton
                size="small"
                onClick={() =>
                  dispatch(
                    deleteWallComment({ postId: post._id!, commentId: c._id! }),
                  )
                }
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        {recorder.recording && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <VoiceNotePlayer
              src={recorder.recording.url}
              durationSec={recorder.recording.durationSec}
            />
            <IconButton
              size="small"
              color="error"
              onClick={recorder.reset}
              title={t("deleteVoice") as string}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        )}
        {attachments.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <AttachmentEditList
              attachments={attachments}
              onRemove={(i) =>
                setAttachments((prev) => prev.filter((_, idx) => idx !== i))
              }
            />
          </Box>
        )}
        <Autocomplete
          multiple
          size="small"
          options={officeUsers}
          value={mentions}
          getOptionLabel={(o) => o.name ?? ""}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          onChange={(_, v) => setMentions(v)}
          sx={{ mb: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("mention")} />
          )}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            fullWidth
            placeholder={
              recorder.isRecording
                ? (t("recording") as string)
                : t("addComment")
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={recorder.isRecording}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
          />
          {recorder.isRecording ? (
            <>
              <IconButton
                size="small"
                color="inherit"
                onClick={recorder.cancel}
                title={t("cancel") as string}
              >
                <Delete fontSize="small" />
              </IconButton>
              <IconButton
                color="error"
                onClick={recorder.stop}
                title={t("voiceNote") as string}
              >
                <Stop />
              </IconButton>
            </>
          ) : (
            !recorder.recording && (
              <IconButton
                color="primary"
                onClick={recorder.start}
                disabled={submitting}
                title={t("recordVoice") as string}
              >
                <Mic />
              </IconButton>
            )
          )}
          {!recorder.isRecording && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                accept="image/*,application/pdf"
                onChange={handleFiles}
              />
              <IconButton
                color="primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || uploadingFiles}
                title={t("attachFiles") as string}
              >
                {uploadingFiles ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AttachFile />
                )}
              </IconButton>
            </>
          )}
          <IconButton
            color="primary"
            onClick={submit}
            disabled={
              submitting ||
              uploadingFiles ||
              recorder.isRecording ||
              (!text.trim() && !recorder.recording && !attachments.length)
            }
          >
            <Send />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}
